import { useMemo } from 'react';
import { useMetricGroupsByKeys } from '@gonfalon/metrics/useMetricGroupsByKeys';
import { useMetricsByKeys } from '@gonfalon/metrics/useMetricsByKeys';

// 5 minutes - matches the staleTime used in MetricsSearch
const METRICS_STALE_TIME = 5 * 60 * 1000;

interface UseMetricsByKeysWithGroupsOptions {
  metricKeys?: string[];
  metricGroupKeys?: string[];
}

/**
 * Fetches metrics by keys and metric groups by keys, then combines them into a single list.
 * Metrics from groups are flattened and included in the result.
 * Note: Metrics from groups are fetched individually to ensure all properties (like `unit`) are available.
 */
export function useMetricsByKeysWithGroups({
  metricKeys = [],
  metricGroupKeys = [],
}: UseMetricsByKeysWithGroupsOptions = {}) {
  const { metrics: loadedMetrics, isPending: isMetricsPending } = useMetricsByKeys(metricKeys, {
    staleTime: METRICS_STALE_TIME,
  });
  const { metricGroups: loadedMetricGroups, isPending: isGroupsPending } = useMetricGroupsByKeys(metricGroupKeys, {
    staleTime: METRICS_STALE_TIME,
  });

  // Extract metric keys from groups to fetch full metric details
  const metricKeysFromGroups = useMemo(() => {
    return (loadedMetricGroups ?? []).flatMap((group) => group.metrics.map((m) => m.key));
  }, [loadedMetricGroups]);

  // Fetch full metric details for metrics from groups
  const { metrics: metricsFromGroups, isPending: isMetricsFromGroupsPending } = useMetricsByKeys(metricKeysFromGroups, {
    staleTime: METRICS_STALE_TIME,
  });

  // Combine individual metrics with metrics from groups, deduplicating by key
  const metrics = useMemo(() => {
    const allMetrics = [...(loadedMetrics ?? []), ...(metricsFromGroups ?? [])];
    // Deduplicate by metric key (in case a metric appears both individually and in a group)
    const seen = new Set<string>();
    return allMetrics.filter((metric) => {
      if (seen.has(metric.key)) {
        return false;
      }
      seen.add(metric.key);
      return true;
    });
  }, [loadedMetrics, metricsFromGroups]);

  return {
    metrics,
    isPending: isMetricsPending || isGroupsPending || isMetricsFromGroupsPending,
  };
}
